import { useState, useEffect, useMemo } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportFormatService } from '../../services/reportFormatService';
import { REPORT_FORMAT_DEFAULTS } from '../../types/reportFormat';
import {
    Box,
    TextField,
    Typography,
    Paper,
    Grid,
    Chip,
    Alert,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Slider,
    Collapse,
    IconButton,
} from '@mui/material';
import { Restaurant, Info, ScaleOutlined, ExpandMore, ExpandLess, LibraryBooks, Assessment, Print, ContentCopy, Send, EditOutlined, Search } from '@mui/icons-material';
import { FOODS_DATA, FoodItem } from '../../data/foodsData';
import { consultationService } from '../../services/consultationService';

// ── Formulas (NRC 2006, from the Calculs sheet) ───────────────────────────────

type Species = 'dog' | 'cat';
type LifeStage = 'puppy' | 'young_adult' | 'adult' | 'senior';
type DogBasicActivity = 'low' | 'normal' | 'high';
type DogSpecialFactor = 'none' | 'hunting' | 'herding' | 'great_dane' | 'newfoundland' | 'nordic';
type CatActivity = 'indoor_strict' | 'goes_out_little' | 'goes_out_a_lot';

function dogBasicActivityFactor(activity: DogBasicActivity): number {
    switch (activity) {
        case 'low': return 0.8;
        case 'high': return 1.2;
        default: return 1.0;
    }
}

function dogSpecialFactorValue(special: DogSpecialFactor): number {
    switch (special) {
        case 'hunting': return 2.0;   // 1.5–3, midpoint 2.0
        case 'herding': return 1.6;   // 1.2–2, midpoint 1.6
        case 'great_dane': return 1.5;
        case 'newfoundland': return 0.8;
        case 'nordic': return 0.9;
        default: return 1.0;
    }
}

function catActivityFactor(activity: CatActivity): number {
    switch (activity) {
        case 'indoor_strict': return 0.8;
        case 'goes_out_little': return 0.9;
        default: return 1.0;
    }
}

function dogLifeStageFactor(stage: LifeStage): number {
    switch (stage) {
        case 'senior': return 90;
        case 'young_adult': return 130;
        default: return 110; // adult
    }
}

/**
 * Dog adult BE: weight^0.75 × stageCoeff × sterilFactor × activityFactor × specialFactor × overweightFactor × customFactor
 */
function calcDogAdultBE(
    weight: number, stage: LifeStage, sterilized: boolean,
    activity: DogBasicActivity, special: DogSpecialFactor,
    overweight: boolean, customFactor: number,
): number {
    return (
        Math.pow(weight, 0.75) *
        dogLifeStageFactor(stage) *
        (sterilized ? 0.8 : 1) *
        dogBasicActivityFactor(activity) *
        dogSpecialFactorValue(special) *
        (overweight ? 0.8 : 1) *
        customFactor
    );
}

/**
 * Cat adult BE: weight^0.67 × sterilFactor × activityFactor × overweightFactor × customFactor × 100
 */
function calcCatAdultBE(
    weight: number, sterilized: boolean,
    activity: CatActivity, overweight: boolean, customFactor: number,
): number {
    return (
        Math.pow(weight, 0.67) *
        (sterilized ? 0.8 : 1) *
        catActivityFactor(activity) *
        (overweight ? 0.8 : 1) *
        customFactor *
        100
    );
}

/** Puppy (dog growth): (254 - 135 × puppyWeight/adultWeight) × puppyWeight^0.75 */
function calcPuppyBE(puppyWeight: number, adultWeight: number): number {
    return (254 - 135 * (puppyWeight / adultWeight)) * Math.pow(puppyWeight, 0.75);
}

/** Kitten (cat growth): 6.7 × (exp(-0.189 × kittenWeight/adultWeight) - 0.66) × 100 × kittenWeight^0.67 */
function calcKittenBE(kittenWeight: number, adultWeight: number): number {
    return 6.7 * (Math.exp(-0.189 * (kittenWeight / adultWeight)) - 0.66) * 100 * Math.pow(kittenWeight, 0.67);
}

/** BE (kcal/jour) → food quantity (g/jour) */
function calcFoodDose(be: number, energyKcalPerKg: number): number {
    return (be / energyKcalPerKg) * 1000;
}

/**
 * NEC → poids de forme.
 * Each point away from 5 = ±10% of ideal weight.
 * current_weight = ideal_weight × (1 + (NEC - 5) × 0.1)
 * → ideal_weight = current_weight / (1 + (NEC - 5) × 0.1)
 */
function calcPoidsDeFormeFromNEC(currentWeight: number, nec: number): number {
    const factor = 1 + (nec - 5) * 0.1;
    if (factor <= 0) return 0;
    return currentWeight / factor;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function speciesFromConsultation(raw: string): Species | null {
    const lower = raw.toLowerCase();
    if (lower.includes('chien') || lower === 'dog') return 'dog';
    if (lower.includes('chat') || lower === 'cat') return 'cat';
    return null;
}

function sterilizedFromConsultation(raw: string): boolean {
    return raw.toLowerCase().includes('stérilisé') && !raw.toLowerCase().includes('non');
}

// ── Sub-component: FoodSelector ───────────────────────────────────────────────

interface FoodSelectorProps {
    label: string;
    species: Species | '';
    foodType: 'dry' | 'wet';
    value: SelectedFood | null;
    onChange: (food: SelectedFood | null) => void;
}

type SelectedFood = FoodItem & { isManual?: boolean };

interface ManualFoodValues {
    name: string;
    brand: string;
    energy: string;
    protein: string;
    phosphorus: string;
    calcium: string;
}

const EMPTY_MANUAL_FOOD: ManualFoodValues = {
    name: '',
    brand: '',
    energy: '',
    protein: '',
    phosphorus: '',
    calcium: '',
};

function parseManualNumber(value: string): number | null {
    if (!value.trim()) return null;
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
}

function FoodSelector({ label, species, foodType, value, onChange }: FoodSelectorProps) {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [sourceMode, setSourceMode] = useState<'database' | 'manual'>('database');
    const [manualValues, setManualValues] = useState<ManualFoodValues>(EMPTY_MANUAL_FOOD);

    const options = useMemo(() =>
        species ? FOODS_DATA.filter(f => f.species === species && f.type === foodType) : [],
        [species, foodType]);

    useEffect(() => {
        onChange(null);
        setInputValue('');
        setManualValues(EMPTY_MANUAL_FOOD);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [species, foodType]);

    const updateManualValue = (field: keyof ManualFoodValues, fieldValue: string) => {
        setManualValues(current => ({ ...current, [field]: fieldValue }));
        if (value?.isManual) onChange(null);
    };

    const energy = parseManualNumber(manualValues.energy);
    const protein = parseManualNumber(manualValues.protein);
    const phosphorus = parseManualNumber(manualValues.phosphorus);
    const calcium = parseManualNumber(manualValues.calcium);
    const hasInvalidPercentage = [protein, phosphorus, calcium].some(
        nutrient => nutrient !== null && (nutrient < 0 || nutrient > 100),
    );
    const canUseManualFood = Boolean(
        species && manualValues.name.trim() && energy !== null && energy > 0 && !hasInvalidPercentage,
    );

    const useManualFood = () => {
        if (!species || !canUseManualFood || energy === null) return;
        onChange({
            id: foodType === 'dry' ? -1 : -2,
            species,
            type: foodType,
            gamme: t('alimentationCalculator.manualFood.customRange'),
            brand: manualValues.brand.trim() || t('alimentationCalculator.manualFood.unbranded'),
            name: manualValues.name.trim(),
            energy_kcal_kg: energy,
            protein_pct: protein,
            rpc: null,
            phosphorus_pct: phosphorus,
            p_g_mcal: null,
            rpp: null,
            calcium_pct: calcium,
            ca_g_mcal: null,
            ca_p_ratio: calcium !== null && phosphorus !== null && phosphorus > 0
                ? Number((calcium / phosphorus).toFixed(2))
                : null,
            isManual: true,
        });
    };

    return (
        <>
            <ToggleButtonGroup
                value={sourceMode}
                exclusive
                size="small"
                fullWidth
                onChange={(_, mode) => {
                    if (!mode) return;
                    setSourceMode(mode);
                    onChange(null);
                }}
                sx={{ mb: 1 }}
            >
                <ToggleButton value="database">
                    <Search sx={{ mr: 0.5, fontSize: '1rem' }} />
                    {t('alimentationCalculator.manualFood.database')}
                </ToggleButton>
                <ToggleButton value="manual">
                    <EditOutlined sx={{ mr: 0.5, fontSize: '1rem' }} />
                    {t('alimentationCalculator.manualFood.manualEntry')}
                </ToggleButton>
            </ToggleButtonGroup>

            {sourceMode === 'database' ? (
                <>
                    <Autocomplete
                        options={options}
                        value={value?.isManual ? null : value}
                        inputValue={inputValue}
                        onInputChange={(_, val) => setInputValue(val)}
                        onChange={(_, val) => onChange(val)}
                        groupBy={(option) => `${option.gamme} — ${option.brand}`}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        size="small"
                        renderInput={(params) => (
                            <TextField {...params} label={label} placeholder="Nom, marque, gamme…" />
                        )}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                                <Box>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg
                                        {option.protein_pct != null && ` · Prot. ${option.protein_pct}%`}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        noOptionsText={!species ? "Sélectionnez l'espèce d'abord" : 'Aucun aliment trouvé'}
                    />
                    {value && !value.isManual && <NutritionChips food={value} />}
                    {!value && <FoodLibrary species={species} foodType={foodType} onSelect={onChange} />}
                </>
            ) : (
                <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {!species && (
                        <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
                            {t('alimentationCalculator.manualFood.selectSpecies')}
                        </Alert>
                    )}
                    <Grid container spacing={1.25}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                label={t('alimentationCalculator.manualFood.name')}
                                value={manualValues.name}
                                onChange={(event) => updateManualValue('name', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('alimentationCalculator.manualFood.brand')}
                                value={manualValues.brand}
                                onChange={(event) => updateManualValue('brand', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                size="small"
                                type="number"
                                label={t('alimentationCalculator.manualFood.energy')}
                                value={manualValues.energy}
                                onChange={(event) => updateManualValue('energy', event.target.value)}
                                inputProps={{ min: 1, step: 1 }}
                                error={energy !== null && energy <= 0}
                                helperText={t('alimentationCalculator.manualFood.energyHelp')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={t('alimentationCalculator.manualFood.protein')}
                                value={manualValues.protein}
                                onChange={(event) => updateManualValue('protein', event.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                                error={protein !== null && (protein < 0 || protein > 100)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={t('alimentationCalculator.manualFood.phosphorus')}
                                value={manualValues.phosphorus}
                                onChange={(event) => updateManualValue('phosphorus', event.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                error={phosphorus !== null && (phosphorus < 0 || phosphorus > 100)}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={t('alimentationCalculator.manualFood.calcium')}
                                value={manualValues.calcium}
                                onChange={(event) => updateManualValue('calcium', event.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                error={calcium !== null && (calcium < 0 || calcium > 100)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={useManualFood}
                                disabled={!canUseManualFood}
                            >
                                {value?.isManual
                                    ? t('alimentationCalculator.manualFood.selected')
                                    : t('alimentationCalculator.manualFood.useFood')}
                            </Button>
                        </Grid>
                    </Grid>
                    {value?.isManual && <NutritionChips food={value} />}
                </Box>
            )}
        </>
    );
}

// ── NutritionChips (shared) ───────────────────────────────────────────────────

function NutritionChips({ food }: { food: FoodItem }) {
    const rppNum = food.rpp ? parseFloat(food.rpp) : NaN;
    return (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
            <Chip size="small" label={`${food.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg`} color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
            {food.protein_pct != null && <Chip size="small" label={`Prot. ${food.protein_pct}%`} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
            {food.rpc != null && <Chip size="small" label={`RPC ${food.rpc} g/Mcal`} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
            {food.phosphorus_pct != null && <Chip size="small" label={`P ${food.phosphorus_pct}%`} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
            {food.calcium_pct != null && <Chip size="small" label={`Ca ${food.calcium_pct}%`} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
            {food.ca_p_ratio != null && <Chip size="small" label={`Ca/P ${food.ca_p_ratio}`} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
            {food.rpp && !isNaN(rppNum) && <Chip size="small" label={`RPP ${rppNum}`} variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />}
            {food.rpp && isNaN(rppNum) && (
                <Chip size="small" label={food.rpp} color="info" variant="outlined" sx={{ fontSize: '0.7rem', height: 22, maxWidth: 220 }} />
            )}
        </Box>
    );
}

// ── FoodLibrary ───────────────────────────────────────────────────────────────

// Pastel colours cycled per brand within each gamme
const BRAND_PALETTE = [
    { border: '#42A5F5', bg: '#E3F2FD' },
    { border: '#AB47BC', bg: '#F3E5F5' },
    { border: '#66BB6A', bg: '#E8F5E9' },
    { border: '#FFA726', bg: '#FFF3E0' },
    { border: '#EC407A', bg: '#FCE4EC' },
    { border: '#26C6DA', bg: '#E0F7FA' },
    { border: '#8D6E63', bg: '#EFEBE9' },
    { border: '#7E57C2', bg: '#EDE7F6' },
];

interface FoodLibraryProps {
    species: Species | '';
    foodType: 'dry' | 'wet';
    onSelect: (food: FoodItem) => void;
}

function FoodLibrary({ species, foodType, onSelect }: FoodLibraryProps) {
    const [openGamme, setOpenGamme] = useState<string | null>(null);

    // Build gamme → brand → items tree
    const tree = useMemo(() => {
        const items = species ? FOODS_DATA.filter(f => f.species === species && f.type === foodType) : [];
        const map: Record<string, Record<string, FoodItem[]>> = {};
        for (const item of items) {
            if (!map[item.gamme]) map[item.gamme] = {};
            if (!map[item.gamme][item.brand]) map[item.gamme][item.brand] = [];
            map[item.gamme][item.brand].push(item);
        }
        return map;
    }, [species, foodType]);

    const gammes = Object.keys(tree);

    if (!species) return null;

    return (
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, gap: 0.5 }}>
                <LibraryBooks sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    Parcourir par gamme
                </Typography>
            </Box>
            {gammes.map((gamme) => {
                const isOpen = openGamme === gamme;
                const brands = tree[gamme];
                const totalItems = Object.values(brands).reduce((s, arr) => s + arr.length, 0);
                return (
                    <Box key={gamme} sx={{ mb: 0.5 }}>
                        {/* Gamme header row */}
                        <Box
                            onClick={() => setOpenGamme(isOpen ? null : gamme)}
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: 1.25, py: 0.6,
                                borderRadius: 1,
                                border: 1,
                                borderColor: isOpen ? 'primary.main' : 'divider',
                                bgcolor: isOpen ? 'primary.light' : 'background.paper',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: isOpen ? 'primary.light' : 'action.hover' },
                                transition: 'all 0.15s',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={isOpen ? 'bold' : 'normal'} noWrap color={isOpen ? 'primary.dark' : 'text.primary'}>
                                    {gamme}
                                </Typography>
                                <Chip size="small" label={totalItems} sx={{ fontSize: '0.65rem', height: 18, bgcolor: 'action.selected' }} />
                            </Box>
                            <IconButton size="small" sx={{ p: 0, ml: 0.5, color: isOpen ? 'primary.main' : 'text.secondary' }}>
                                {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                        </Box>

                        <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ pl: 1, mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {Object.entries(brands).map(([brand, items], brandIdx) => {
                                    const color = BRAND_PALETTE[brandIdx % BRAND_PALETTE.length];
                                    return (
                                        <Box
                                            key={brand}
                                            sx={{
                                                borderLeft: 3,
                                                borderColor: color.border,
                                                borderRadius: '0 4px 4px 0',
                                                bgcolor: color.bg,
                                                px: 1,
                                                py: 0.5,
                                            }}
                                        >
                                            {/* Brand header */}
                                            <Typography variant="caption" fontWeight="bold" sx={{ color: color.border, display: 'block', mb: 0.25 }}>
                                                {brand}
                                            </Typography>
                                            {/* Compact item rows */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                {items.map((food) => (
                                                    <Box
                                                        key={food.id}
                                                        onClick={() => onSelect(food)}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 1,
                                                            px: 0.75,
                                                            py: 0.4,
                                                            borderRadius: 0.75,
                                                            cursor: 'pointer',
                                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                                                            transition: 'background 0.1s',
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ lineHeight: 1.3, flex: 1, minWidth: 0 }} noWrap>
                                                            {food.name}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={`${food.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg`}
                                                            sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0, bgcolor: 'white', borderColor: color.border, border: 1 }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Collapse>
                    </Box>
                );
            })}
        </Box>
    );
}

// ── NEC marks ─────────────────────────────────────────────────────────────────

const NEC_MARKS = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => ({
    value: n,
    label: String(n),
}));

function necDescription(nec: number): string {
    switch (nec) {
        case 1: return 'Émaciation sévère';
        case 2: return 'Très maigre';
        case 3: return 'Maigre';
        case 4: return 'Sous-poids léger';
        case 5: return 'Poids de forme';
        case 6: return 'Surpoids léger';
        case 7: return 'Surpoids modéré';
        case 8: return 'Obésité';
        case 9: return 'Obésité sévère';
        default: return '';
    }
}

// ── Component Props ───────────────────────────────────────────────────────────

interface PatientInfo {
    name?: string;
    breed?: string;
    age?: string;
    gender?: string;
}

function toWeightNum(v: number | null | undefined): number {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
}

interface AlimentationCalculatorProps {
    weight?: number | null;
    species?: string;
    sterilizationStatus?: string;
    patientInfo?: PatientInfo;
    consultationId?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function AlimentationCalculator({
    weight,
    species: speciesRaw,
    sterilizationStatus,
    patientInfo,
    consultationId,
}: AlimentationCalculatorProps) {
    const detectedSpecies = speciesRaw ? speciesFromConsultation(speciesRaw) : null;
    const detectedSterilized = sterilizationStatus ? sterilizedFromConsultation(sterilizationStatus) : false;
    const isNarrow = window.self !== window.top; // extension side-panel

    // ── Animal parameters ─────────────────────────────────────────────────────
    const [species, setSpecies] = useState<Species | ''>(detectedSpecies ?? '');
    const [lifeStage, setLifeStage] = useState<LifeStage>('adult');

    // Weight input mode
    const [weightMode, setWeightMode] = useState<'ideal' | 'nec'>('ideal');
    const [idealWeight, setIdealWeight] = useState<number>(toWeightNum(weight));
    const [currentWeightForNec, setCurrentWeightForNec] = useState<number>(toWeightNum(weight));
    const [nec, setNec] = useState<number>(5);

    const [adultWeight, setAdultWeight] = useState<number>(0); // for puppy/kitten

    const [sterilized, setSterilized] = useState<boolean>(detectedSterilized);
    const [dogActivity, setDogActivity] = useState<DogBasicActivity>('normal');
    const [dogSpecial, setDogSpecial] = useState<DogSpecialFactor>('none');
    const [catActivity, setCatActivity] = useState<CatActivity>('indoor_strict');
    // overweight: auto-set from NEC when in NEC mode, manual otherwise
    const [overweightManual, setOverweightManual] = useState<boolean>(false);
    const [customFactor, setCustomFactor] = useState<number>(1);

    // ── Food selection mode ───────────────────────────────────────────────────
    const [dietMode, setDietMode] = useState<'single' | 'mixed'>('single');
    const [singleFoodType, setSingleFoodType] = useState<'dry' | 'wet'>('dry');
    const [singleFood, setSingleFood] = useState<SelectedFood | null>(null);
    const [mixedDryPct, setMixedDryPct] = useState<number>(70);
    const [mixedDryFood, setMixedDryFood] = useState<SelectedFood | null>(null);
    const [mixedWetFood, setMixedWetFood] = useState<SelectedFood | null>(null);

    // ── Sync from consultation props ──────────────────────────────────────────
    useEffect(() => { if (detectedSpecies) setSpecies(detectedSpecies); }, [speciesRaw]);
    useEffect(() => {
        const w = toWeightNum(weight);
        if (w > 0) {
            setIdealWeight(w);
            setCurrentWeightForNec(w);
        }
    }, [weight]);
    useEffect(() => { setSterilized(detectedSterilized); }, [sterilizationStatus]);

    // ── Derived values ────────────────────────────────────────────────────────
    const poidsDeFormeFromNec = useMemo(() =>
        weightMode === 'nec' && currentWeightForNec > 0
            ? calcPoidsDeFormeFromNEC(currentWeightForNec, nec)
            : 0,
        [weightMode, currentWeightForNec, nec]);

    // The weight used in formulas is always the ideal/forme weight
    const weightForCalc = weightMode === 'nec' ? poidsDeFormeFromNec : idealWeight;

    // Overweight: auto-derived from NEC > 5 in NEC mode, otherwise manual
    const overweight = weightMode === 'nec' ? nec > 5 : overweightManual;

    const isPuppy = lifeStage === 'puppy';

    // ── BE calculation ────────────────────────────────────────────────────────
    const be = useMemo((): number | null => {
        if (!species || !weightForCalc || weightForCalc <= 0) return null;

        if (isPuppy) {
            if (!adultWeight || adultWeight <= 0) return null;
            if (species === 'dog') return calcPuppyBE(weightForCalc, adultWeight);
            return calcKittenBE(weightForCalc, adultWeight);
        }

        if (species === 'dog') {
            return calcDogAdultBE(weightForCalc, lifeStage, sterilized, dogActivity, dogSpecial, overweight, customFactor);
        }
        return calcCatAdultBE(weightForCalc, sterilized, catActivity, overweight, customFactor);
    }, [species, weightForCalc, isPuppy, adultWeight, lifeStage, sterilized, dogActivity, dogSpecial, catActivity, overweight, customFactor]);

    // ── Dose calculation ──────────────────────────────────────────────────────
    const doseResult = useMemo(() => {
        if (be === null || be <= 0) return null;
        if (dietMode === 'single') {
            if (!singleFood) return null;
            return { type: 'single' as const, dose: Math.round(calcFoodDose(be, singleFood.energy_kcal_kg)), food: singleFood };
        }
        // mixed
        if (!mixedDryFood && !mixedWetFood) return null;
        const wetPct = 100 - mixedDryPct;
        const dryDose = mixedDryFood ? Math.round(calcFoodDose(be * mixedDryPct / 100, mixedDryFood.energy_kcal_kg)) : null;
        const wetDose = mixedWetFood ? Math.round(calcFoodDose(be * wetPct / 100, mixedWetFood.energy_kcal_kg)) : null;
        return { type: 'mixed' as const, dryDose, wetDose, dryPct: mixedDryPct, wetPct };
    }, [be, dietMode, singleFood, mixedDryFood, mixedWetFood, mixedDryPct]);

    // ── Puppy ratio check ─────────────────────────────────────────────────────
    const puppyWarning = isPuppy && adultWeight > 0 && weightForCalc >= adultWeight
        ? 'Le poids actuel est ≥ au poids adulte estimé. Vérifiez les valeurs.'
        : null;

    // ── Alimentation report ───────────────────────────────────────────────────
    const [alimentationReport, setAlimentationReport] = useState<string>('');
    const [reportEmail, setReportEmail] = useState<string>('');
    const [emailSending, setEmailSending] = useState(false);
    const [emailStatus, setEmailStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const { data: reportFormat } = useQuery({
        queryKey: ['reportFormat', 'alimentation_report'],
        queryFn: () => reportFormatService.getFormat('alimentation_report'),
        retry: false,
        throwOnError: false,
    });

    const generateReport = () => {
        const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
        const speciesWord = species === 'dog' ? 'chien' : species === 'cat' ? 'chat' : '';
        const lifeStageLabel: Record<LifeStage, string> = {
            puppy: species === 'cat' ? 'Chaton (croissance)' : 'Chiot (croissance)',
            young_adult: 'Jeune adulte (1–2 ans)',
            adult: 'Adulte',
            senior: 'Sénior',
        };

        // ── Intro sentence ──────────────────────────────────────────────────
        const name    = patientInfo?.name?.trim();
        const breed   = patientInfo?.breed?.trim();
        const age     = patientInfo?.age?.trim();
        const genderRaw = patientInfo?.gender?.trim()?.toLowerCase() ?? '';
        const isFeminine = genderRaw.includes('femel');

        const subjectParts = [breed, speciesWord].filter(Boolean).join(' ');
        const subjectStr   = name
            ? `Pour ${name}, ${subjectParts || speciesWord}`
            : `Pour ce ${subjectParts || speciesWord}`;

        const detailParts: string[] = [];
        if (genderRaw && !['inconnu', 'unknown', ''].includes(genderRaw)) detailParts.push(genderRaw);
        if (!isPuppy) detailParts.push(sterilized ? (isFeminine ? 'stérilisée' : 'stérilisé') : (isFeminine ? 'non stérilisée' : 'non stérilisé'));
        if (lifeStage !== 'adult') detailParts.push(lifeStageLabel[lifeStage].toLowerCase());
        if (age) detailParts.push(`de ${age}`);
        const introSentence = detailParts.length
            ? `${subjectStr} ${detailParts.join(' ')}.`
            : `${subjectStr}.`;

        const reportTitle = name ? `Guide nutritionnel — ${name}` : 'Guide nutritionnel';

        // ── Build markdown ──────────────────────────────────────────────────
        const lines: string[] = [];
        lines.push(`### ${reportTitle}`);
        lines.push('');
        lines.push(`**Date :** ${today}`);
        lines.push('');
        lines.push(introSentence);
        lines.push('');
        lines.push('---');
        lines.push('');

        // Weight block — cast to Number() to guard against string-typed values at runtime
        const wCurrent = Number(currentWeightForNec);
        const wForme = Number(weightForCalc);
        const wForme2 = Number(poidsDeFormeFromNec);
        const wAdult = Number(adultWeight);
        lines.push('#### Poids');
        if (weightMode === 'nec' && wCurrent > 0) {
            lines.push(`- **Poids actuel :** ${wCurrent.toFixed(1).replace('.', ',')} kg — NEC ${nec}/9 (${necDescription(nec)})`);
            lines.push(`- **Poids de forme calculé :** ${wForme2.toFixed(2).replace('.', ',')} kg`);
        } else {
            lines.push(`- **Poids de forme :** ${wForme.toFixed(1).replace('.', ',')} kg`);
        }
        if (isPuppy && wAdult > 0) {
            lines.push(`- **Poids adulte estimé :** ${wAdult.toFixed(1).replace('.', ',')} kg`);
        }
        lines.push('');
        lines.push('---');
        lines.push('');

        // BE block
        lines.push('#### Besoin énergétique journalier (NRC 2006)');
        if (be !== null && be > 0) {
            lines.push(`- **Besoin calculé :** **${Math.round(be).toLocaleString('fr-FR')} kcal/jour**`);
        } else {
            lines.push('- Données insuffisantes pour calculer le besoin.');
        }
        lines.push('');
        lines.push('---');
        lines.push('');

        // Ration block
        lines.push('#### Ration journalière recommandée');
        lines.push('');

        if (doseResult?.type === 'single') {
            const f = doseResult.food;
            const typeLabel = f.type === 'dry' ? 'Croquettes' : 'Aliment humide';
            lines.push(`**${typeLabel} — ${f.brand} — ${f.name}** (${f.gamme})`);
            lines.push('');
            lines.push(`- Densité énergétique : ${f.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg`);
            lines.push(`- **Ration journalière : ${doseResult.dose.toLocaleString('fr-FR')} g/jour**`);
            lines.push('');

            // Alternatives from same gamme
            if (be !== null && be > 0 && !f.isManual) {
                const alternatives = FOODS_DATA.filter(alt =>
                    alt.species === species &&
                    alt.type === f.type &&
                    alt.gamme === f.gamme &&
                    alt.id !== f.id
                );
                if (alternatives.length > 0) {
                    lines.push(`#### Alternatives de la même gamme (${f.gamme})`);
                    lines.push('');
                    for (const alt of alternatives.slice(0, 12)) {
                        const altDose = Math.round(calcFoodDose(be, alt.energy_kcal_kg));
                        lines.push(`- ${alt.brand} — ${alt.name} : **${altDose.toLocaleString('fr-FR')} g/jour**`);
                    }
                    lines.push('');
                }
            }
        } else if (doseResult?.type === 'mixed') {
            lines.push(`**Régime mixte :** ${doseResult.dryPct}% croquettes / ${doseResult.wetPct}% humide`);
            lines.push('');
            if (doseResult.dryDose !== null && mixedDryFood) {
                const f = mixedDryFood;
                const beShare = Math.round(be! * doseResult.dryPct / 100);
                lines.push(`**Croquettes (${doseResult.dryPct}%) — ${f.brand} — ${f.name}** (${f.gamme})`);
                lines.push(`- Densité : ${f.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg | Apport : ${beShare.toLocaleString('fr-FR')} kcal/jour`);
                lines.push(`- **Ration : ${doseResult.dryDose.toLocaleString('fr-FR')} g/jour**`);
                lines.push('');
            }
            if (doseResult.wetDose !== null && mixedWetFood) {
                const f = mixedWetFood;
                const beShare = Math.round(be! * doseResult.wetPct / 100);
                lines.push(`**Humide (${doseResult.wetPct}%) — ${f.brand} — ${f.name}** (${f.gamme})`);
                lines.push(`- Densité : ${f.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg | Apport : ${beShare.toLocaleString('fr-FR')} kcal/jour`);
                lines.push(`- **Ration : ${doseResult.wetDose.toLocaleString('fr-FR')} g/jour**`);
            }
        } else {
            lines.push('Aucun aliment sélectionné.');
        }

        lines.push('---');
        lines.push('');
        lines.push('> **Important :** Ces recommandations sont données à titre indicatif. La ration doit être ajustée selon l\'évolution du poids et de la condition corporelle (NEC) de l\'animal. Peser les aliments avec précision. Consultez votre vétérinaire pour tout suivi.');

        setAlimentationReport(lines.join('\n'));
    };

    const handlePrintReport = () => {
        if (!alimentationReport) return;
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        const htmlContent = renderToStaticMarkup(<ReactMarkdown>{alimentationReport}</ReactMarkdown>);

        const fmt = reportFormat;
        const defaults = REPORT_FORMAT_DEFAULTS.alimentation_report;
        const accent = fmt?.accent_color || defaults.accent_color || '#27ae60';
        const font = fmt?.font_family || defaults.font_family || "'Segoe UI', sans-serif";
        const headerFontSize = fmt?.header_font_size || 10;
        const headerTextColor = fmt?.header_text_color || '#555555';
        const headerText = fmt?.header_text || '';
        const footerText = fmt?.footer_text || '';
        const headerImage = fmt?.header_image || null;
        const customClosing = fmt?.custom_closing || '';

        // header_text is now rich HTML from the TipTap editor — inject directly.
        const headerHtml = (headerText || headerImage)
            ? `<div class="doc-header">
                 ${headerImage ? `<img src="${headerImage}" class="header-logo" alt="logo" />` : ''}
                 ${headerText ? `<div class="header-rich">${headerText}</div>` : ''}
               </div>`
            : '';

        const footerHtml = footerText
            ? `<div class="doc-footer">${footerText.replace(/\n/g, '<br/>')}</div>`
            : '';

        const closingHtml = customClosing
            ? `<div class="doc-closing">${customClosing.replace(/\n/g, '<br/>')}</div>`
            : '';

        printWindow.document.write(`<!DOCTYPE html><html><head><title>Guide nutritionnel</title>
            <style>
                @page { size: auto; margin: 0; }
                body { font-family: ${font}, sans-serif; padding: 14mm 16mm; line-height: 1.5; color: #333; font-size: 11px; }
                .doc-header { display: flex; align-items: flex-start; gap: 12px; padding-bottom: 10px; border-bottom: 2px solid ${accent}; margin-bottom: 14px; }
                .header-logo { max-height: 60px; max-width: 130px; object-fit: contain; flex-shrink: 0; }
                .header-rich { font-size: 10px; flex: 1; min-width: 0; }
                .header-rich p { margin: 0; }
                h3 { color: #2c3e50; border-bottom: 2px solid ${accent}; padding-bottom: 6px; font-size: 15px; margin-bottom: 10px; }
                h4 { color: ${accent}; font-size: 12px; margin: 10px 0 4px; }
                strong { color: #2c3e50; }
                ul, ol { padding-left: 18px; margin: 4px 0; }
                li { margin-bottom: 2px; }
                p { margin-bottom: 8px; }
                hr { border: none; border-top: 1px solid #ddd; margin: 8px 0; }
                blockquote { border-left: 3px solid #f39c12; padding-left: 10px; color: #666; margin: 0; font-size: 10px; }
                .doc-closing { margin-top: 12px; font-style: italic; color: #555; }
                .doc-footer { margin-top: 20px; padding-top: 8px; border-top: 1px solid #ddd; font-size: 9px; color: #888; }
            </style></head><body>${headerHtml}${htmlContent}${closingHtml}${footerHtml}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* ── Animal parameters ── */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Restaurant sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight="bold">Paramètres de l'animal</Typography>
                </Box>

                <Grid container spacing={1.5}>

                    {/* Species + Life stage */}
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Espèce</InputLabel>
                            <Select value={species} label="Espèce" onChange={(e) => setSpecies(e.target.value as Species)}>
                                <MenuItem value="dog">Chien</MenuItem>
                                <MenuItem value="cat">Chat</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Stade physiologique</InputLabel>
                            <Select value={lifeStage} label="Stade physiologique" onChange={(e) => setLifeStage(e.target.value as LifeStage)}>
                                <MenuItem value="puppy">{species === 'cat' ? 'Chaton' : 'Chiot'}</MenuItem>
                                <MenuItem value="young_adult">Jeune adulte (1–2 ans)</MenuItem>
                                <MenuItem value="adult">Adulte</MenuItem>
                                <MenuItem value="senior">Sénior</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Weight input mode (only for adults) */}
                    {!isPuppy && (
                        <Grid item xs={12}>
                            <ToggleButtonGroup
                                value={weightMode}
                                exclusive
                                onChange={(_, val) => { if (val) setWeightMode(val); }}
                                size="small"
                                fullWidth
                            >
                                <ToggleButton value="ideal" sx={isNarrow ? { fontSize: '0.7rem', py: 0.5, px: 0.75, lineHeight: 1.2 } : {}}>
                                    <ScaleOutlined sx={{ mr: 0.5, fontSize: isNarrow ? '0.85rem' : '1rem' }} />
                                    {isNarrow ? 'Poids de forme' : 'Poids de forme direct'}
                                </ToggleButton>
                                <ToggleButton value="nec" sx={isNarrow ? { fontSize: '0.7rem', py: 0.5, px: 0.75, lineHeight: 1.2 } : {}}>
                                    {isNarrow ? 'Poids actuel + NEC' : 'Poids actuel + NEC'}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    )}

                    {/* Direct poids de forme */}
                    {(!isPuppy && weightMode === 'ideal') && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth size="small"
                                label="Poids de forme / NEC 5/9 (kg)"
                                type="number"
                                value={idealWeight > 0 ? idealWeight : ''}
                                onChange={(e) => { const v = parseFloat(e.target.value); setIdealWeight(isNaN(v) ? 0 : v); }}
                                inputProps={{ min: 0, step: 0.1 }}
                            />
                        </Grid>
                    )}

                    {/* Puppy weight fields */}
                    {isPuppy && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small"
                                    label={species === 'cat' ? 'Poids chaton (kg)' : 'Poids chiot (kg)'}
                                    type="number"
                                    value={idealWeight > 0 ? idealWeight : ''}
                                    onChange={(e) => { const v = parseFloat(e.target.value); setIdealWeight(isNaN(v) ? 0 : v); }}
                                    inputProps={{ min: 0, step: 0.1 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth size="small"
                                    label="Poids adulte estimé (kg)"
                                    type="number"
                                    value={adultWeight > 0 ? adultWeight : ''}
                                    onChange={(e) => { const v = parseFloat(e.target.value); setAdultWeight(isNaN(v) ? 0 : v); }}
                                    inputProps={{ min: 0, step: 0.1 }}
                                />
                            </Grid>
                        </>
                    )}

                    {/* NEC mode fields */}
                    {!isPuppy && weightMode === 'nec' && (
                        <>
                            <Grid item xs={5}>
                                <TextField
                                    fullWidth size="small"
                                    label="Poids actuel (kg)"
                                    type="number"
                                    value={currentWeightForNec > 0 ? currentWeightForNec : ''}
                                    onChange={(e) => { const v = parseFloat(e.target.value); setCurrentWeightForNec(isNaN(v) ? 0 : v); }}
                                    inputProps={{ min: 0, step: 0.1 }}
                                />
                            </Grid>
                            <Grid item xs={7}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, px: 0.5 }}>
                                    NEC : <strong>{nec}/9</strong> — {necDescription(nec)}
                                </Typography>
                                <Slider
                                    value={nec}
                                    min={1} max={9} step={1}
                                    marks={NEC_MARKS}
                                    onChange={(_, val) => setNec(val as number)}
                                    size="small"
                                    sx={{ mx: 0.5, width: 'calc(100% - 8px)' }}
                                    color={nec > 5 ? 'warning' : nec < 5 ? 'secondary' : 'primary'}
                                />
                            </Grid>

                            {/* Poids de forme computed */}
                            {currentWeightForNec > 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ScaleOutlined fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            Poids de forme calculé :&nbsp;
                                            <strong>{poidsDeFormeFromNec.toFixed(2)} kg</strong>
                                            <Typography component="span" variant="caption" color="text.secondary">
                                                {' '}({nec > 5
                                                    ? `−${((1 - poidsDeFormeFromNec / currentWeightForNec) * 100).toFixed(0)}% du poids actuel`
                                                    : nec < 5
                                                        ? `+${((poidsDeFormeFromNec / currentWeightForNec - 1) * 100).toFixed(0)}% du poids actuel`
                                                        : 'poids idéal'})
                                            </Typography>
                                        </Typography>
                                        {nec > 5 && (
                                            <Chip size="small" label="Surpoids détecté" color="warning" sx={{ fontSize: '0.7rem', height: 20, ml: 'auto' }} />
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* Sterilized (not for puppy) */}
                    {!isPuppy && (
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stérilisé(e)</InputLabel>
                                <Select value={sterilized ? 'yes' : 'no'} label="Stérilisé(e)" onChange={(e) => setSterilized(e.target.value === 'yes')}>
                                    <MenuItem value="yes">Oui</MenuItem>
                                    <MenuItem value="no">Non</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Overweight — manual only when not using NEC mode */}
                    {!isPuppy && weightMode === 'ideal' && (
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Surpoids</InputLabel>
                                <Select value={overweightManual ? 'yes' : 'no'} label="Surpoids" onChange={(e) => setOverweightManual(e.target.value === 'yes')}>
                                    <MenuItem value="no">Non</MenuItem>
                                    <MenuItem value="yes">Oui</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    {/* Overweight indicator when in NEC mode (read-only) */}
                    {!isPuppy && weightMode === 'nec' && (
                        <Grid item xs={6}>
                            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.875, height: '100%', display: 'flex', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Surpoids :</Typography>
                                <Typography variant="body2" fontWeight="bold" color={overweight ? 'warning.main' : 'text.primary'}>
                                    {overweight ? 'Oui (NEC > 5)' : 'Non'}
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    {/* Dog: Basic activity */}
                    {!isPuppy && species === 'dog' && (
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Activité physique</InputLabel>
                                <Select value={dogActivity} label="Activité physique" onChange={(e) => setDogActivity(e.target.value as DogBasicActivity)}>
                                    <MenuItem value="low">Faible (&lt;1h/jour)</MenuItem>
                                    <MenuItem value="normal">Normale</MenuItem>
                                    <MenuItem value="high">Élevée</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Dog: Special breed/usage factor */}
                    {!isPuppy && species === 'dog' && (
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Race / usage particulier</InputLabel>
                                <Select value={dogSpecial} label="Race / usage particulier" onChange={(e) => setDogSpecial(e.target.value as DogSpecialFactor)}>
                                    <MenuItem value="none">Aucun (×1,0)</MenuItem>
                                    <MenuItem value="hunting">Chasse (×2,0)</MenuItem>
                                    <MenuItem value="herding">Chien de troupeau (×1,6)</MenuItem>
                                    <MenuItem value="great_dane">Grand Danois (×1,5)</MenuItem>
                                    <MenuItem value="newfoundland">Terre-Neuve (×0,8)</MenuItem>
                                    <MenuItem value="nordic">Chiens nordiques (×0,9)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Cat: activity/lifestyle */}
                    {!isPuppy && species === 'cat' && (
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Mode de vie</InputLabel>
                                <Select value={catActivity} label="Mode de vie" onChange={(e) => setCatActivity(e.target.value as CatActivity)}>
                                    <MenuItem value="indoor_strict">Intérieur strict</MenuItem>
                                    <MenuItem value="goes_out_little">Sort un peu</MenuItem>
                                    <MenuItem value="goes_out_a_lot">Sort beaucoup</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Custom factor (not for puppy) */}
                    {!isPuppy && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth size="small"
                                label="Facteur correcteur supplémentaire (1 par défaut)"
                                type="number"
                                value={customFactor}
                                onChange={(e) => setCustomFactor(parseFloat(e.target.value) || 1)}
                                inputProps={{ min: 0.1, step: 0.05 }}
                                InputProps={{
                                    endAdornment: (
                                        <Tooltip title="Facteur multiplicateur final appliqué sur le besoin calculé. Laisser à 1 si aucune correction spécifique.">
                                            <Info fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                                        </Tooltip>
                                    ),
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* ── Calculated BE ── */}
            {be !== null && be > 0 ? (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.light' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.dark" gutterBottom>
                        Besoin énergétique journalier (NRC 2006)
                    </Typography>
                    <Typography variant="h5" color="primary.dark" fontWeight="bold">
                        {Math.round(be).toLocaleString('fr-FR')} kcal/jour
                    </Typography>
                    {puppyWarning && (
                        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>{puppyWarning}</Alert>
                    )}
                </Paper>
            ) : (weightForCalc > 0 || (weightMode === 'nec' && currentWeightForNec > 0)) && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                    {!species
                        ? "Sélectionnez l'espèce pour calculer le besoin énergétique."
                        : isPuppy && (!adultWeight || adultWeight <= 0)
                            ? 'Renseignez le poids adulte estimé pour le calcul en croissance.'
                            : 'Renseignez le poids pour calculer.'}
                </Alert>
            )}

            <Divider />

            {/* ── Food selection ── */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Sélection de l'aliment
                </Typography>

                {/* Diet mode toggle */}
                <ToggleButtonGroup
                    value={dietMode}
                    exclusive
                    onChange={(_, val) => { if (val) setDietMode(val); }}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                >
                    <ToggleButton value="single" sx={isNarrow ? { fontSize: '0.7rem', py: 0.5, px: 0.75, lineHeight: 1.2 } : {}}>Aliment seul</ToggleButton>
                    <ToggleButton value="mixed" sx={isNarrow ? { fontSize: '0.7rem', py: 0.5, px: 0.75, lineHeight: 1.2 } : {}}>Régime mixte</ToggleButton>
                </ToggleButtonGroup>

                {dietMode === 'single' && (
                    <>
                        <ToggleButtonGroup
                            value={singleFoodType}
                            exclusive
                            onChange={(_, val) => { if (val) setSingleFoodType(val); }}
                            size="small"
                            fullWidth
                            sx={{ mb: 1.5 }}
                        >
                            <ToggleButton value="dry" sx={isNarrow ? { fontSize: '0.7rem', py: 0.5, px: 0.75, lineHeight: 1.2 } : {}}>Croquettes</ToggleButton>
                            <ToggleButton value="wet" sx={isNarrow ? { fontSize: '0.7rem', py: 0.5, px: 0.75, lineHeight: 1.2 } : {}}>Humide</ToggleButton>
                        </ToggleButtonGroup>
                        <FoodSelector
                            label="Rechercher un aliment"
                            species={species}
                            foodType={singleFoodType}
                            value={singleFood}
                            onChange={setSingleFood}
                        />
                    </>
                )}

                {dietMode === 'mixed' && (
                    <>
                        {/* Dry/Wet split slider */}
                        <Box sx={{ px: 1, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Croquettes : <strong>{mixedDryPct}%</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Humide : <strong>{100 - mixedDryPct}%</strong>
                                </Typography>
                            </Box>
                            <Slider
                                value={mixedDryPct}
                                min={10} max={90} step={5}
                                onChange={(_, val) => setMixedDryPct(val as number)}
                                size="small"
                                marks={[
                                    { value: 10, label: '10%' },
                                    { value: 50, label: '50%' },
                                    { value: 90, label: '90%' },
                                ]}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `Croc. ${v}%`}
                            />
                        </Box>

                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                    Croquettes ({mixedDryPct}%)
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <FoodSelector
                                        label="Croquettes"
                                        species={species}
                                        foodType="dry"
                                        value={mixedDryFood}
                                        onChange={setMixedDryFood}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                    Humide ({100 - mixedDryPct}%)
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <FoodSelector
                                        label="Aliment humide"
                                        species={species}
                                        foodType="wet"
                                        value={mixedWetFood}
                                        onChange={setMixedWetFood}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Paper>

            {/* ── Final result ── */}
            {doseResult && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="success.dark" gutterBottom>
                        Ration journalière recommandée
                    </Typography>

                    {doseResult.type === 'single' && (
                        <>
                            <Typography variant="h4" color="success.dark" fontWeight="bold">
                                {doseResult.dose.toLocaleString('fr-FR')} g/jour
                            </Typography>
                            <Typography variant="caption" color="success.dark">
                                {Math.round(be!).toLocaleString('fr-FR')} kcal/jour ÷ {(doseResult.food.energy_kcal_kg / 1000).toFixed(3)} kcal/g
                            </Typography>
                        </>
                    )}

                    {doseResult.type === 'mixed' && (
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            {doseResult.dryDose !== null && mixedDryFood && (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 1.5, bgcolor: 'success.main', borderRadius: 1 }}>
                                        <Typography variant="caption" color="success.contrastText" display="block">
                                            Croquettes ({doseResult.dryPct}%)
                                        </Typography>
                                        <Typography variant="h6" color="success.contrastText" fontWeight="bold">
                                            {doseResult.dryDose.toLocaleString('fr-FR')} g/jour
                                        </Typography>
                                        <Typography variant="caption" color="success.contrastText">
                                            {mixedDryFood.name} — {mixedDryFood.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {doseResult.wetDose !== null && mixedWetFood && (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 1.5, bgcolor: 'success.dark', borderRadius: 1 }}>
                                        <Typography variant="caption" color="success.contrastText" display="block">
                                            Humide ({doseResult.wetPct}%)
                                        </Typography>
                                        <Typography variant="h6" color="success.contrastText" fontWeight="bold">
                                            {doseResult.wetDose.toLocaleString('fr-FR')} g/jour
                                        </Typography>
                                        <Typography variant="caption" color="success.contrastText">
                                            {mixedWetFood.name} — {mixedWetFood.energy_kcal_kg.toLocaleString('fr-FR')} kcal/kg
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {(doseResult.dryDose !== null || doseResult.wetDose !== null) && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="success.dark">
                                        Total : {Math.round(be!).toLocaleString('fr-FR')} kcal/jour
                                        {doseResult.dryDose !== null && doseResult.wetDose !== null
                                            ? ` (${doseResult.dryDose + doseResult.wetDose} g total)`
                                            : ''}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, fontSize: '0.75rem', '& .MuiAlert-message': { py: 0.5 } }}>
                        Indication théorique — ajuster selon la condition corporelle et les recommandations du fabricant.
                    </Alert>
                </Paper>
            )}

            {/* ── Alimentation report ── */}
            {doseResult && (
                <>
                    <Divider />
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Assessment sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
                                <Typography variant="subtitle2" fontWeight="bold">Rapport d'alimentation</Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={<Assessment />}
                                onClick={generateReport}
                            >
                                Générer
                            </Button>
                        </Box>

                        {alimentationReport && (
                            <>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={8}
                                    value={alimentationReport}
                                    onChange={(e) => setAlimentationReport(e.target.value)}
                                    sx={{
                                        mb: 1.5,
                                        '& textarea': { fontSize: '0.8125rem', lineHeight: 1.5 },
                                    }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="E-mail du destinataire"
                                        type="email"
                                        value={reportEmail}
                                        onChange={(e) => setReportEmail(e.target.value)}
                                    />
                                    {emailStatus && (
                                        <Alert severity={emailStatus.type} onClose={() => setEmailStatus(null)} sx={{ py: 0 }}>
                                            {emailStatus.message}
                                        </Alert>
                                    )}
                                    <Button
                                        variant="outlined"
                                        startIcon={<Send />}
                                        fullWidth
                                        disabled={!reportEmail.trim() || emailSending || !consultationId}
                                        onClick={async () => {
                                            if (!consultationId) return;
                                            setEmailSending(true);
                                            setEmailStatus(null);
                                            try {
                                                await consultationService.sendAlimentationReportEmail(
                                                    consultationId,
                                                    reportEmail.trim(),
                                                    alimentationReport,
                                                );
                                                setEmailStatus({ type: 'success', message: `Email envoyé à ${reportEmail}` });
                                            } catch {
                                                setEmailStatus({ type: 'error', message: "Échec de l'envoi. Vérifiez la configuration SMTP." });
                                            } finally {
                                                setEmailSending(false);
                                            }
                                        }}
                                    >
                                        {emailSending ? 'Envoi…' : 'Envoyer par e-mail'}
                                    </Button>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<Print />}
                                            onClick={handlePrintReport}
                                            sx={{ flex: 1 }}
                                        >
                                            Imprimer
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ContentCopy />}
                                            onClick={() => navigator.clipboard.writeText(alimentationReport)}
                                            sx={{ flex: 1 }}
                                        >
                                            Copier
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Paper>
                </>
            )}
        </Box>
    );
}
